/**
 * AJ Lead Capture — client-side triggers.
 *
 * Each trigger arms a single fire and returns a disarm function. Exit-intent
 * falls back to a 30s inactivity timer on touch devices (no reliable mobile
 * mouseleave).
 *
 * @module triggers
 */

const MOBILE_EXIT_FALLBACK_S = 30;

/**
 * Whether this is a touch device.
 *
 * @returns {boolean} Touch capability.
 */
function isTouch() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Current scroll depth as a percentage (0–100).
 *
 * @returns {number} Percent scrolled.
 */
function scrollPercent() {
  const doc = document.documentElement;
  const height = doc.scrollHeight - window.innerHeight;
  if (height <= 0) {
    return 100;
  }
  return ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
}

/**
 * Fire `fn` once, guarding against double-invocation.
 *
 * @param {Function} fn Callback.
 * @returns {Function} Wrapped one-shot callback.
 */
function once(fn) {
  let done = false;
  return () => {
    if (done) {
      return;
    }
    done = true;
    fn();
  };
}

/**
 * Arm an inactivity timer.
 *
 * @param {number}   seconds Idle seconds before firing.
 * @param {Function} fire    One-shot callback.
 * @returns {Function} Disarm.
 */
function armInactivity(seconds, fire) {
  let timer = null;
  const events = ['mousemove', 'keydown', 'scroll', 'touchstart'];

  const reset = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(fire, seconds * 1000);
  };

  events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
  reset();

  return () => {
    window.clearTimeout(timer);
    events.forEach((ev) => window.removeEventListener(ev, reset));
  };
}

/**
 * Arm a trigger.
 *
 * @param {string}   trigger exit_intent | inactivity | time_delay | scroll_percent
 * @param {number}   value   Seconds or percent (per trigger).
 * @param {Function} onFire  Callback when the trigger fires.
 * @returns {Function} Disarm function.
 */
export function arm(trigger, value, onFire) {
  const fire = once(onFire);

  if (trigger === 'exit_intent') {
    if (isTouch()) {
      return armInactivity(MOBILE_EXIT_FALLBACK_S, fire);
    }
    const onLeave = (e) => {
      if (e.clientY <= 10 && scrollPercent() < 80) {
        fire();
      }
    };
    document.documentElement.addEventListener('mouseleave', onLeave);
    return () => document.documentElement.removeEventListener('mouseleave', onLeave);
  }

  if (trigger === 'inactivity') {
    return armInactivity(value || MOBILE_EXIT_FALLBACK_S, fire);
  }

  if (trigger === 'time_delay') {
    const t = window.setTimeout(fire, (value || 0) * 1000);
    return () => window.clearTimeout(t);
  }

  if (trigger === 'scroll_percent') {
    let ticking = false;
    const onScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        if (scrollPercent() >= (value || 0)) {
          fire();
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }

  // shortcode / unknown — no automatic trigger.
  return () => {};
}
